/**
 * Panorama da Carteira (#57).
 *
 * Composição, movimento e qualidade das vidas — sobre a base rica
 * `public.esau_beneficiarios` (a mesma da busca/kanban).
 *
 * Overdelivery: cada card tem tooltip explicando "o que é / de onde vem", e
 * clicar em qualquer gráfico/card abre o detalhe de QUEM compõe aquele número
 * (/radar-carteira/panorama-carteira/detalhe).
 */
import React, { useEffect, useState } from 'react';
import { Users, RefreshCw, TrendingUp, AlertTriangle, X } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, PieChart, Pie, Cell,
} from 'recharts';
import api from '../services/api';
import SharedInfoTip from '../components/InfoTip';

const CORES = ['#20a490', '#3498db', '#9b59b6', '#e67e22', '#27ae60', '#e74c3c',
  '#f1c40f', '#1abc9c', '#34495e', '#fd79a8', '#00b894', '#6c5ce7'];

const nfmt = (n) => (n ?? 0).toLocaleString('pt-BR');

// ── Tooltip explicativo (ⓘ): o compartilhado (portal, não é cortado) ─────
const InfoTip = ({ texto }) => <SharedInfoTip texto={texto} w={230} cor="var(--gray-400)" style={{ marginLeft: 6 }} />;

const Card = ({ children, span }) => (
  <div style={{
    background: 'var(--card-bg, #fff)', border: '1px solid var(--gray-200)',
    borderRadius: 10, padding: '1rem 1.1rem', gridColumn: span ? `span ${span}` : undefined,
  }}>{children}</div>
);

const Titulo = ({ children, dica }) => (
  <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', color: 'var(--gray-700)', display: 'flex', alignItems: 'center' }}>
    {children}{dica && <InfoTip texto={dica} />}
  </h3>
);

const Kpi = ({ label, valor, cor, dica, onClick }) => (
  <Card>
    <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', display: 'flex', alignItems: 'center' }}>
      {label}{dica && <InfoTip texto={dica} />}
    </div>
    <div onClick={onClick}
      style={{ fontSize: '1.8rem', fontWeight: 700, color: cor || 'var(--gray-800)', cursor: onClick ? 'pointer' : 'default' }}>
      {nfmt(valor)}
    </div>
  </Card>
);

const Pizza = ({ dados, onSelect }) => (
  <ResponsiveContainer width="100%" height={240}>
    <PieChart>
      <Pie data={dados} dataKey="total" nameKey="rotulo" cx="50%" cy="50%" outerRadius={85}
        label={(e) => `${e.rotulo}: ${nfmt(e.total)}`} labelLine={false} fontSize={11}
        onClick={(e) => onSelect && e && onSelect(e.rotulo ?? e.payload?.rotulo)}
        style={{ cursor: onSelect ? 'pointer' : 'default' }}>
        {(dados || []).map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
      </Pie>
      <Tooltip formatter={(v) => nfmt(v)} />
    </PieChart>
  </ResponsiveContainer>
);

const BarrasH = ({ dados, cor = '#20a490', onSelect }) => (
  <ResponsiveContainer width="100%" height={Math.max(200, (dados?.length || 0) * 30)}>
    <BarChart data={dados} layout="vertical" margin={{ left: 20, right: 20 }}>
      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
      <XAxis type="number" tick={{ fontSize: 11 }} />
      <YAxis type="category" dataKey="rotulo" width={150} tick={{ fontSize: 11 }} />
      <Tooltip formatter={(v) => nfmt(v)} />
      <Bar dataKey="total" fill={cor} radius={[0, 4, 4, 0]}
        onClick={(d) => onSelect && d && onSelect(d.rotulo ?? d.payload?.rotulo)}
        style={{ cursor: onSelect ? 'pointer' : 'default' }} />
    </BarChart>
  </ResponsiveContainer>
);

const BarrasV = ({ dados, cor, chaveX = 'rotulo', chaveY = 'total', chaveSelect, onSelect }) => {
  const kSel = chaveSelect || chaveX;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={dados}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={chaveX} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v) => nfmt(v)} />
        <Bar dataKey={chaveY} fill={cor} radius={[4, 4, 0, 0]}
          onClick={(d) => onSelect && d && onSelect(d[kSel] ?? d.payload?.[kSel])}
          style={{ cursor: onSelect ? 'pointer' : 'default' }} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ── Modal de detalhe (quem compõe) ──────────────────────────────────────────
const ModalDetalhe = ({ titulo, data, loading, onClose }) => (
  <div onClick={onClose} style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 50,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
  }}>
    <div onClick={(e) => e.stopPropagation()} style={{
      background: '#fff', borderRadius: 10, width: 'min(920px, 96vw)', maxHeight: '86vh',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.9rem 1.1rem', borderBottom: '1px solid var(--gray-200)' }}>
        <div>
          <div style={{ fontWeight: 700 }}>{titulo}</div>
          {data && (
            <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
              {nfmt(data.total)} vida(s){data.total > (data.itens?.length || 0) ? ` · mostrando ${nfmt(data.itens.length)}` : ''}
            </div>
          )}
        </div>
        <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--gray-500)' }}><X size={20} /></button>
      </div>
      <div style={{ overflow: 'auto', padding: '0.5rem 1.1rem 1.1rem' }}>
        {loading ? <p>Carregando…</p> : !data || !data.itens?.length ? (
          <p style={{ color: 'var(--gray-500)' }}>Nada a mostrar.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead><tr style={{ color: 'var(--gray-500)', textAlign: 'left', position: 'sticky', top: 0, background: '#fff' }}>
              {['Matrícula', 'Nome', 'Tipo', 'Sexo', 'Idade', 'Plano', 'Empresa', 'UF', 'Adesão'].map((h) => (
                <th key={h} style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.itens.map((it, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '0.4rem 0.5rem', fontFamily: 'monospace' }}>{it.matricula}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{it.nome}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{it.tipo}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{it.sexo}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{it.idade ?? '—'}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{it.plano}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{it.empresa}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{it.uf}</td>
                  <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>{it.adesao || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </div>
);

const PanoramaCarteira = () => {
  const [dados, setDados] = useState(null);
  const [qual, setQual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const [det, setDet] = useState(null);        // {titulo}
  const [detData, setDetData] = useState(null);
  const [detLoading, setDetLoading] = useState(false);

  const carregar = async () => {
    setLoading(true); setErro(null);
    try {
      const [pan, q] = await Promise.all([
        api.get('/radar-carteira/panorama-carteira'),
        api.get('/radar-carteira/qualidade-cadastro'),
      ]);
      setDados(pan.data);
      setQual(q.data);
    } catch (e) {
      setErro('Não foi possível carregar o panorama da carteira.');
    }
    setLoading(false);
  };

  useEffect(() => { carregar(); }, []);

  const abrir = async (dim, valor, titulo) => {
    if (valor == null || valor === '') return;
    setDet({ titulo }); setDetData(null); setDetLoading(true);
    try {
      const { data } = await api.get('/radar-carteira/panorama-carteira/detalhe',
        { params: { dim, valor, limite: 500 } });
      setDetData(data);
    } catch (e) {
      setDetData({ total: 0, itens: [] });
    }
    setDetLoading(false);
  };
  const fechar = () => { setDet(null); setDetData(null); };

  const tipoTotal = (rot) =>
    (dados?.por_tipo || []).find((t) => (t.rotulo || '').toUpperCase() === rot)?.total || 0;

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.5rem', margin: 0 }}>
            <Users size={24} /> Panorama da Carteira
          </h1>
          <p style={{ color: 'var(--gray-600)', marginTop: '0.25rem', fontSize: '0.9rem' }}>
            Composição, movimento e qualidade sobre a base rica de beneficiários (a mesma da busca).
          </p>
        </div>
        <button className="btn btn-secondary" onClick={carregar} disabled={loading}>
          <RefreshCw size={16} /> {loading ? 'Carregando...' : 'Atualizar'}
        </button>
      </div>
      <p style={{ color: 'var(--gray-500)', fontSize: '0.8rem', margin: '0 0 1rem' }}>
        💡 Clique em qualquer card ou gráfico para ver <strong>quem compõe</strong> o número.
      </p>

      {erro && <Card><div style={{ color: 'var(--danger, #e74c3c)' }}>{erro}</div></Card>}
      {loading && !dados && <Card>Carregando…</Card>}

      {dados && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
          <Kpi label="Total de vidas" valor={dados.total}
            dica="Todas as vidas na base (ativas + inativas). Fonte: cadastro sincronizado de beneficiários (esau_beneficiarios), a mesma base da Busca." />
          <Kpi label="Ativas" valor={dados.ativos} cor="#20a490"
            dica="Vidas com status ATIVO. Clique para ver a lista."
            onClick={() => abrir('status', 'ATIVO', 'Vidas ativas')} />
          <Kpi label="Titulares" valor={tipoTotal('TITULAR')} cor="#3498db"
            dica="Entre as ativas, os titulares (parentesco = TITULAR)."
            onClick={() => abrir('tipo', 'TITULAR', 'Titulares (ativos)')} />
          <Kpi label="Dependentes" valor={tipoTotal('DEPENDENTE')} cor="#9b59b6"
            dica="Entre as ativas, os dependentes (todo parentesco diferente de TITULAR)."
            onClick={() => abrir('tipo', 'DEPENDENTE', 'Dependentes (ativos)')} />

          <Card span={2}>
            <Titulo dica="Novas adesões por mês (campo de data de adesão), últimos 24 meses. Clique num mês para ver quem entrou.">
              <TrendingUp size={15} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Entradas por mês: adesão (24 meses)
            </Titulo>
            <BarrasV dados={dados.movimento} cor="#27ae60" chaveX="mes_br" chaveY="entradas" chaveSelect="mes"
              onSelect={(m) => abrir('mes_adesao', m, `Entradas em ${m.slice(5, 7)}/${m.slice(0, 4)}`)} />
          </Card>

          <Card>
            <Titulo dica="Distribuição de TODAS as vidas por status (ativo/inativo).">Por status</Titulo>
            <Pizza dados={dados.por_status} onSelect={(v) => abrir('status', v, `Status: ${v}`)} />
          </Card>
          <Card>
            <Titulo dica="Vidas ativas por sexo (M/F/I). Vem do cadastro.">Por sexo</Titulo>
            <Pizza dados={dados.por_sexo} onSelect={(v) => abrir('sexo', v, `Sexo: ${v}`)} />
          </Card>

          <Card span={2}>
            <Titulo dica="Vidas ativas por faixa de idade, calculada da data de nascimento.">Por faixa etária (ativas)</Titulo>
            <BarrasV dados={dados.por_faixa} cor="#3498db"
              onSelect={(v) => abrir('faixa', v, `Faixa etária: ${v}`)} />
          </Card>

          <Card span={2}>
            <Titulo dica="Vidas ativas por plano contratado (top 12).">Por plano (top 12)</Titulo>
            <BarrasH dados={dados.por_plano} onSelect={(v) => abrir('plano', v, `Plano: ${v}`)} />
          </Card>
          <Card span={2}>
            <Titulo dica="Vidas ativas por empresa (código do contratante). Top 12.">Por empresa (top 12)</Titulo>
            <BarrasH dados={dados.por_empresa} cor="#e67e22"
              onSelect={(v) => abrir('empresa', v, v)} />
          </Card>
          <Card span={2}>
            <Titulo dica="Vidas ativas por UF do endereço cadastrado.">Por estado</Titulo>
            <BarrasV dados={dados.por_estado} cor="#9b59b6"
              onSelect={(v) => abrir('estado', v, `Estado: ${v}`)} />
          </Card>
        </div>
      )}

      {qual && (
        <div style={{ marginTop: '1rem' }}>
          <Card>
            <Titulo dica="Vidas ativas com algum campo ausente/inválido no cadastro. Clique numa linha para ver quais vidas.">
              <AlertTriangle size={15} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Qualidade do cadastro (ativos)
            </Titulo>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: 'var(--gray-600)' }}>
              <strong style={{ color: qual.pct_com_pendencia > 0 ? '#e67e22' : '#27ae60' }}>
                {nfmt(qual.com_pendencia)} de {nfmt(qual.total)} ({qual.pct_com_pendencia}%)
              </strong> têm ao menos uma pendência de cadastro.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ color: 'var(--gray-500)', textAlign: 'left' }}>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Pendência</th>
                  <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>Quantidade</th>
                  <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>% dos ativos</th>
                </tr></thead>
                <tbody>
                  {qual.itens.map((it) => {
                    const clicavel = it.chave !== 'matricula_duplicada' && it.quantidade > 0;
                    return (
                      <tr key={it.chave}
                        onClick={clicavel ? () => abrir('pendencia', it.chave, it.rotulo) : undefined}
                        style={{ borderTop: '1px solid var(--gray-100)', cursor: clicavel ? 'pointer' : 'default' }}>
                        <td style={{ padding: '0.4rem 0.6rem', color: clicavel ? '#2563eb' : 'inherit' }}>{it.rotulo}</td>
                        <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', fontWeight: 600 }}>{nfmt(it.quantidade)}</td>
                        <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: 'var(--gray-500)' }}>
                          {it.pct == null ? '—' : `${it.pct}%`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {det && <ModalDetalhe titulo={det.titulo} data={detData} loading={detLoading} onClose={fechar} />}
    </div>
  );
};

export default PanoramaCarteira;
